import { CircularProgress } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useOutletContext, useParams } from "react-router";
import styled from "styled-components";
import AppContext from "../../../AppContext";
import { APP_TITLE } from "../../../config/constant";
import LayoutContext from "../../../LayoutContext";
import PopupContext from "../../../PopupContext";
import * as Library from "../../../utils/Library";
import { getAllUnlockedNavigations, userIsOnBoardingInPage } from "../../../utils/onboardingHelper";
import { setNavigations } from "../../../utils/redux/navigationAccessSlice";
import { triggeredTypes } from "../../../utils/utilHelpers";
import AddressFormCardStandard from "../../common/forms/AddressFormCardStandard";
import FormCard from "../../common/forms/FormCard";
import FormField from "../../common/forms/FormField";
import FormFieldImageUpload from "../../common/forms/FormFieldImageUpload";
import SimpleButtonContentHeader from "../../common/mainApp/mainWindowHeaders/SimpleButtonContentHeader";
import DeliveryOptionsAvailable from "./createStoreComponents/DeliveryOptionsAvailable";
import StoreHours from "./createStoreComponents/StoreHours";
import StorePopups from "./createStoreComponents/StorePopups";
import StoreStockSharing from "./createStoreComponents/StoreStockSharing";
import { Tooltip } from "antd";

// #region SCOPED CSS
const StyledPage = styled.div`
    .input-remove-default {
        border: none;
        outline: none;
        background: none;
    }

    .selects {
        background: var(--backgrounds-and-lines-n-600, #10141b);
    }

    .selects.mandatory {
        border: 1px solid transparent;
        border-left: 5px solid var(--text-icons-orange-500);
        border-radius: 4px;
        outline: none;
    }

    .selects.mandatory > div {
        border: none;
        cursor: pointer;
    }

    .selects.mandatory.error {
        border: 1px solid var(--text-icons-red-500);
        border-left: 5px solid var(--text-icons-red-500);
    }

    .main-window-header {
        background: var(--backgrounds-and-lines-n-600, #10141b);
        display: flex;
        flex-direction: row;
        row-gap: 0px;
        align-items: center;
        justify-content: space-between;
        flex-shrink: 0;
        height: 68px;
        position: relative;
    }
    .frame-40 {
        display: flex;
        flex-direction: row;
        gap: 8px;
        align-items: center;
        justify-content: flex-end;
        flex: 1;
        position: relative;
    }
    .btn-large-solid {
        background: var(--buttons-all-platforms-gray-300, #1e2631);
        border-radius: 4px;
        padding: 10px;
        display: flex;
        flex-direction: row;
        gap: 10px;
        row-gap: 0px;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 120px;
        height: 35px;
        position: relative;
    }
    .button {
        color: var(--buttons-all-platforms-gray-200, #4f5b6d);
        text-align: left;
        font-size: var(--button-text-small-font-size, 10px);
        letter-spacing: var(--button-text-small-letter-spacing, 0.0125em);
        font-weight: var(--button-text-small-font-weight, 400);
        text-transform: uppercase;
        position: relative;
    }
    .frame-1000011239,
    .frame-1000011239 * {
        box-sizing: border-box;
    }
    .frame-1000011239 {
        background: var(--backgrounds-and-lines-n-100, #3c485a);
        border-radius: 9999px;
        padding: 2px 8px 2px 8px;
        display: flex;
        flex-direction: row;
        gap: 10px;
        align-items: center;
        justify-content: center;
        align-self: stretch;
        flex-shrink: 0;
        position: relative;
        cursor: pointer;
    }
    .fetch-from-google-maps {
        color: #ffffff;
        text-align: left;
        font-family: var(--label-xs-uppercase-font-family, "Roboto-Medium", sans-serif);
        font-size: var(--label-xs-uppercase-font-size, 8px);
        letter-spacing: var(--label-xs-uppercase-letter-spacing, 0.0125em);
        font-weight: var(--label-xs-uppercase-font-weight, 500);
        text-transform: uppercase;
        position: relative;
        display: flex;
        gap: 5px;
    }
    .circular-loader {
        width: 10px !important;
        height: 10px !important;
    }
    .disabled-open-hours-btn {
        cursor: not-allowed;
        opacity: 0.3;
    }
`;
// #endregion

export default function CreateStorePage() {
    const params = useParams();
    const storeID = params?.storeID;
    const isCreatePage = !storeID;
    document.title = `${APP_TITLE} - ${isCreatePage ? "Create Store" : "Edit Store"}`;

    // #region STATES
    const userIsLoggedIn = !!localStorage.getItem("token");
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { MainLayoutProps, layoutType } = useOutletContext();

    const { showIsStorePosSavedPopup, setShowIsStorePosSavedPopup } = useContext(LayoutContext);
    const { setShowStoreWithoutPOSOnboardingCompleted } = useContext(PopupContext);

    const { setHasUnsavedChanges, userData } = useContext(AppContext);
    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };
    const navigations = useSelector(state => state.navigationAccesses);
    const userAppData = useSelector(state => state.userData.userAppData);

    // eslint-disable-next-line no-unused-vars
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingStoreData, setIsLoadingStoreData] = useState(false);
    const [isOpeningHoursLoading, setIsOpeningHoursLoading] = useState(false);
    const [saveButtonEnabled, setSaveButtonEnabled] = useState(true);
    const [showDialogBox, setShowDialogBox] = useState(false);
    const [editStoreLoading, setEditStoreLoading] = useState(false);
    const [createStoreLoading, setCreateStoreLoading] = useState(false);
    const [storeId, setStoreId] = useState("");
    const [isOpenHoursBtnEnabled, setIsOpenHoursBtnEnabled] = useState(false);
    const [openingHoursError, setOpeningHoursError] = useState({ error: false, status: 0 });

    const isUserOnboarding = userIsOnBoardingInPage(navigations?.navigationAccesses, "stores");

    const [retailBrands, setRetailBrands] = useState([]);

    const [storeFormData, setStoreFormData] = useState({
        retailBrandID: "",
        retailBrandName: "",
        storeName: "",
        storeFacadeData: null,
        storeInteriorData: null,
        googlePlusCode: "",
        country: "",
        city: "",
        postalCode: "",
        addressLine1: "",
        addressLine2: "",
        latitude: "",
        longitude: "",
        pickupEnabled: true,
        courierEnabled: false,
        shippingEnabled: false,
        shoppingCenter: "false",
        shoppingCenterFloor: "",
        shoppingCenterName: "",
        shoppingCenterStoreNumber: "",
        storeHoursData: {
            monday: { hourStart: "", hourEnd: "", closedAllDay: true },
            tuesday: { hourStart: "", hourEnd: "", closedAllDay: true },
            wednesday: { hourStart: "", hourEnd: "", closedAllDay: true },
            thursday: { hourStart: "", hourEnd: "", closedAllDay: true },
            friday: { hourStart: "", hourEnd: "", closedAllDay: true },
            saturday: { hourStart: "", hourEnd: "", closedAllDay: true },
            sunday: { hourStart: "", hourEnd: "", closedAllDay: true }
        }
    });

    // #endregion

    // #region STOCK SHARING STATES
    const [limitedStock, setLimitedStock] = useState(false);
    const [fullStock, setFullStock] = useState(false);

    const [brandIsInvalid, setBrandIsInvalid] = useState(false);
    const [stockSharingSelected, setStockSharingSelected] = useState({
        shareStockDisabled: false,
        agreeTerms: false,
        shareDropdownEnabled: false,
        loading: false
    });

    const [retailBrandConnectedBrands, setRetailBrandConnectedBrands] = useState([]);
    const [stockSharingToLabelBrands, setStockSharingToLabelBrands] = useState([]);
    // #endregion

    // #region STATES FOR STORE PAGE
    const [showTAC, setShowTAC] = useState(false);
    const [showCorrectCompanyPopup, setShowCorrectCompanyPopup] = useState(false);
    const [correctCompanyChecked, setCorrectCompanyChecked] = useState(false);
    const [googleMatchError, setGoogleMatchError] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        if (!retailBrands) return;
        const currentRetailBrand = sessionStorage.getItem("currentRetailBrand") || "";
        const currentRetailBrandObject = retailBrands?.find(item => item.value === currentRetailBrand);
        const defaultRetailBrand = localStorage.getItem("defaultRetailBrand") || "";
        const defaultRetailBrandObject = retailBrands?.find(item => item.value === defaultRetailBrand);
        const value = currentRetailBrandObject || defaultRetailBrandObject || retailBrands?.[0];

        setStoreFormData({
            ...storeFormData,
            retailBrandID: value?.value || "",
            retailBrandName: value?.name || ""
        });
    }, [retailBrands]);

    useEffect(() => {
        if (!userData) return;
        const defaultRetailBrandID = localStorage.getItem("defaultRetailBrand");
        const selectedRetailBrandID = storeFormData?.retailBrandID;

        const retailBrandData = userData?.retailBrands?.map(item => {
            let retailBrandName = item.name;

            if (item?._id === defaultRetailBrandID) {
                retailBrandName = `${item?.name} (default)`;
            }
            return {
                name: retailBrandName,
                value: item._id,
                selected: item._id.toString() === selectedRetailBrandID.toString()
            };
        });

        if (retailBrandData?.length === 1) retailBrandData[0].selected = true;

        setRetailBrands(retailBrandData);
    }, [userData]);

    useEffect(() => {
        if (!userData) return;

        const isEdit = !!storeID;
        const storeNavigation = navigations?.navigationAccesses?.stores;
        const userHasStoreAccess = storeNavigation?.navigationPanel && !storeNavigation?.createModeOnly;

        if (isEdit && !userHasStoreAccess) {
            navigate("/");

            Library.showToastMessage({
                type: "warning",
                title: "Warning",
                message: "You do not have access to this page yet. Redirecting to onboarding."
            });
            return;
        }
    }, [userData]);

    /* useEffect(() => {
        if (!storeID) {
            setStoreFormData({ ...storeFormData, storeName: retailBrandData?.name });
            return;
        }
    }, [storeID]); */

    /* useEffect(() => {
        if (!navigations?.navigationAccesses) return;

        //Verify if user has already unlocked the stores pages
        const storeNavigation = navigations?.navigationAccesses?.stores;
        const userHasStoreAccess = storeNavigation?.navigationPanel && !storeNavigation?.createModeOnly;

        if (!userHasStoreAccess) {
            Library.showToastMessage({
                type: "warning",
                title: "Warning",
                message: "You do not have access to this page yet. Redirecting to onboarding."
            });

            navigate("/");

            return;
        }
    }, [navigations]); */

    useEffect(() => {
        setupHeaders(
            props,
            saveButtonEnabled,
            () => {
                setShowDialogBox(true);
            },
            storeID,
            isUserOnboarding,
            navigations
        );
    }, [saveButtonEnabled, isLoading, navigations, isUserOnboarding]);

    useEffect(() => {
        Library.scrollWindowToTop();

        if (!userIsLoggedIn) {
            Library.showToastMessage({
                type: "warning",
                title: "Warning",
                message: "Please login to access this page."
            });

            return;
        }

        getRetailBrandRequest();
        //if (storeID) getStoreData();

        if (!storeID) {
            const disableShowCorrectCompanyPopup = localStorage.getItem("disableShowCorrectCompanyPopup");
            if (disableShowCorrectCompanyPopup === "true") return;
            setShowCorrectCompanyPopup(true);
        }
    }, []);

    useEffect(() => {
        checkFormIsValid();
    }, [stockSharingSelected, limitedStock, fullStock, stockSharingToLabelBrands]);

    /*const debouncedStoreName = useDebounce(storeFormData?.storeName, 1000);
    useEffect(() => {
        if (debouncedStoreName?.trim() !== "") {
            fetchGoogleMapsInfo(storeFormData?.storeName?.trim());
        }
    }, [debouncedStoreName]);*/
    // #endregion

    // #region METHODS
    const fetchGoogleMapsInfo = async storeName => {
        if (!storeName) return;

        const res = await Library.makeCommonPostRequest(
            "Stores",
            "fetchGoogleMapsPlacesData",
            { storeName },
            false,
            setIsOpeningHoursLoading,
            undefined,
            undefined,
            false
        );

        if (res?.data?.status === 200) {
            setGoogleMatchError(false);
            const googleData = res?.data?.data;
            const openHours = googleData?.openingHours?.response?.openingHours;
            setStoreFormData(prev => ({
                ...prev,
                storeHoursData: {
                    monday: readStoreHoursForDay(openHours, "monday"),
                    tuesday: readStoreHoursForDay(openHours, "tuesday"),
                    wednesday: readStoreHoursForDay(openHours, "wednesday"),
                    thursday: readStoreHoursForDay(openHours, "thursday"),
                    friday: readStoreHoursForDay(openHours, "friday"),
                    saturday: readStoreHoursForDay(openHours, "saturday"),
                    sunday: readStoreHoursForDay(openHours, "sunday")
                },
                googlePlusCode: googleData?.plusCode || prev.googlePlusCode,
                country: googleData?.country || prev.country,
                city: googleData?.city || prev.city,
                postalCode: googleData?.postalCode || prev.postalCode,
                addressLine1: googleData?.address1 || prev.addressLine1,
                addressLine2: googleData?.address2 || prev.addressLine2,
                latitude: googleData?.latitude || prev.latitude,
                longitude: googleData?.longitude || prev.longitude,
                shoppingCenter: googleData?.shoppingCenter === "Yes" ? "true" : "false"
            }));
        } else {
            setGoogleMatchError(true);
        }
    };

    const getStoreData = async () => {
        if (!userIsLoggedIn) return;
        const formData = {
            id: storeID
        };
        const res = await Library.makeCommonPostRequest(
            "Stores",
            "getStoreData",
            formData,
            false,
            setIsLoadingStoreData,
            undefined,
            undefined,
            false
        );
        const data = res?.data?.data;
        const status = res?.data?.status;

        if (status === 404) {
            Library.showToastMessage({
                type: "warning",
                title: "Store not found",
                message: "The store you are looking for does not exist."
            });

            navigate("/stores");
            return;
        } else if (status === 401) {
            Library.showToastMessage({
                type: "warning",
                title: "Unauthorized",
                message: "You are not authorized to access this store."
            });

            navigate("/login");
            return;
        }

        setStoreId(data?._id);

        const { openHours } = data || {};

        setStoreFormData({
            retailBrandID: data?.retailBrandID || "",
            retailBrandName: data?.retailBrandData?.name || "",
            storeName: data?.name || "",
            storeFacadeData: data?.pictureURL || null,
            storeInteriorData: data?.interiorURL || null,
            googlePlusCode: data?.googlePlusCode || "",
            country: data?.country || "",
            city: data?.city || "",
            postalCode: data?.postalCode || "",
            addressLine1: data?.address || "",
            addressLine2: data?.addressNumber || "",
            shoppingCenter: data?.shoppingCenter?.toString() || "",
            latitude: data?.latitude || "",
            longitude: data?.longitude || "",
            pickupEnabled: data?.pickupEnabled || false,
            courierEnabled: data?.courierEnabled || false,
            shippingEnabled: data?.shippingEnabled || false,
            shoppingCenterFloor: data?.shoppingCenterFloor || "",
            shoppingCenterName: data?.shoppingCenterName || "",
            shoppingCenterStoreNumber: data?.shoppingCenterStoreNumber || "",

            storeHoursData: {
                monday: readStoreHoursForDay(openHours, "monday"),
                tuesday: readStoreHoursForDay(openHours, "tuesday"),
                wednesday: readStoreHoursForDay(openHours, "wednesday"),
                thursday: readStoreHoursForDay(openHours, "thursday"),
                friday: readStoreHoursForDay(openHours, "friday"),
                saturday: readStoreHoursForDay(openHours, "saturday"),
                sunday: readStoreHoursForDay(openHours, "sunday")
            }
        });

        const stockSharingOnlyBOImages = data?.stockSharing === "BOimages";
        const stockSharingAll = data?.stockSharing === "All";
        const stockSharingNo = data?.stockSharing === "No";

        const stockSharingLabelBrandIDs = data?.stockSharingLabelBrandIDs || [];

        if (stockSharingOnlyBOImages || stockSharingAll) {
            setLimitedStock(stockSharingOnlyBOImages);
            setFullStock(stockSharingAll);

            setStockSharingSelected({
                shareStockDisabled: false,
                agreeTerms: true,
                shareDropdownEnabled: true,
                loading: false
            });

            getConnectedRetailBrands(null, stockSharingLabelBrandIDs);
        } else if (stockSharingNo) {
            setLimitedStock(false);
            setFullStock(false);

            setStockSharingSelected({
                shareStockDisabled: true,
                agreeTerms: false,
                shareDropdownEnabled: false,
                loading: false
            });
        }
    };

    const readStoreHoursForDay = (data, dayStr) => {
        const dayData = data?.[dayStr];
        if (!data) return { hourStart: "", hourEnd: "", closedAllDay: false, openAllDay: false };

        const res = {
            hourStart: dayData?.hourStart || "",
            hourEnd: dayData?.hourEnd || "",
            closedAllDay: dayData?.closedAllDay || false,
            openAllDay: dayData?.openAllDay || false
        };

        return res;
    };

    const checkFormIsValid = (highlightValidation = false) => {
        const allCompanyIdFieldsAreValid = formCompanyID.current.allFieldsAreValid(highlightValidation);
        const allAddressFieldsAreValid = formCompanyAddress.current.allFieldsAreValid(highlightValidation);
        const allStoreHoursFieldsAreValid = formStoreHoursRef.current.allFieldsAreValid(highlightValidation);
        const allDeliveryOptionsFieldsAreValid = formDeliveryOptionsRef.current.allFieldsAreValid(highlightValidation);
        const isStoreNameValid = formCompanyFieldsRefs.storeName.isValid(highlightValidation);
        const isGooglePlusCodeValid = !!storeFormData.googlePlusCode;

        const allFieldsAreValid =
            allCompanyIdFieldsAreValid &&
            allAddressFieldsAreValid &&
            allStoreHoursFieldsAreValid &&
            allDeliveryOptionsFieldsAreValid;

        setIsOpenHoursBtnEnabled(isStoreNameValid && isGooglePlusCodeValid);

        let stockSharingIsValid = true;
        const hasStockSharingSelected = stockSharingToLabelBrands?.some(brand => brand?.selected);

        if (!stockSharingSelected.shareStockDisabled) {
            //if (!stockSharingToLabelBrands?.length && (limitedStock || fullStock)) {
            if (!hasStockSharingSelected && (limitedStock || fullStock)) {
                stockSharingIsValid = false;
                //if (stockSharingSelected.agreeTerms) setBrandIsInvalid(true);
            } else setBrandIsInvalid(false);

            if (!stockSharingSelected.agreeTerms) {
                stockSharingIsValid = false;
            }
            if (!limitedStock && !fullStock) {
                stockSharingIsValid = false;
            }
        }
        if (stockSharingIsValid) setBrandIsInvalid(false);

        const enableSaveButton = allFieldsAreValid && stockSharingIsValid;
        setSaveButtonEnabled(enableSaveButton);

        return allFieldsAreValid;
    };

    const getRetailBrandRequest = async () => {
        //TODO: fetch from selected retail brand ID
        const postData = {
            retailBrandID: JSON.parse(localStorage.getItem("retailBrands"))?.[0]?._id
        };
        const res = await Library.makePostRequest("getRetailBrand", postData, false, setIsLoading);
        if (res?.data?.status === 200) {
            const retailBrandData = res.data?.data;

            if (isCreatePage && retailBrandData?.stockSharingLabelBrandIDs) {
                setLimitedStock(retailBrandData?.stockSharing === "BOimages");
                setFullStock(retailBrandData?.stockSharing === "All");

                setStockSharingSelected({
                    shareStockDisabled: false,
                    agreeTerms: true,
                    shareDropdownEnabled: true,
                    loading: false
                });
                getConnectedRetailBrands(null, retailBrandData.stockSharingLabelBrandIDs);
            }
        }

        if (!isCreatePage) getStoreData();
    };

    const onSaveClicked = async () => {
        const storeIDValues = formCompanyID.current.getAllFieldValues();
        const locationValues = formCompanyAddress.current.getAllFieldValues();
        const storeHours = formStoreHoursRef.current.getAllFieldValues();
        const deliveryOptions = formDeliveryOptionsRef.current.getAllFieldValues();

        const postData = {
            retailBrandID: storeIDValues?.toBrand?.value,
            storeID: storeId,
            name: storeIDValues?.storeName,
            facadeImageData: storeIDValues?.storeFacade?.url || storeIDValues?.storeFacade,
            interiorImageData: storeIDValues?.storeInterior?.url || storeIDValues?.storeInterior,
            latitude: locationValues?.latitude,
            longitude: locationValues?.longitude,
            googlePlusCode: locationValues?.googlePlusCode,
            country: locationValues?.country?.name,
            city: locationValues?.city?.name,
            postalCode: locationValues?.postalCode,
            address: locationValues?.addressOne,
            addressNumber: locationValues?.addressTwo,
            shoppingCenter: locationValues?.shoppingCenter?.value === "true" ? "yes" : "no",
            openHours: storeHours,
            pickupEnabled: deliveryOptions?.pickupEnabled,
            courierEnabled: deliveryOptions?.courierEnabled,
            shippingEnabled: deliveryOptions?.shippingEnabled,

            shoppingCenterName: locationValues?.shoppingCenterName,
            shoppingCenterStoreNumber: locationValues?.shoppingCenterStoreNumber,
            shoppingCenterFloor: locationValues?.shoppingCenterFloor
        };

        if (!stockSharingSelected.shareStockDisabled) {
            if (fullStock) postData.stockSharing = "All";
            else if (limitedStock) postData.stockSharing = "BOimages";

            const selectedBrands = stockSharingToLabelBrands?.map(item => item?.value);
            const areAllSelected = selectedBrands?.length === retailBrandConnectedBrands?.length;
            const isAllLabelBrandsSelected = selectedBrands[0] === "allLabelBrands";

            if (areAllSelected || isAllLabelBrandsSelected) postData.stockSharingLabelBrandIDs = [];
            else postData.stockSharingLabelBrandIDs = selectedBrands;
        } else postData.stockSharing = "No";

        setHasUnsavedChanges(false);

        if (storeID) {
            const res = await Library.makeCommonPostRequest(
                "Stores",
                "updateStore",
                postData,
                false,
                setEditStoreLoading
            );
            if (res?.data?.status === 200) {
                Library.showUpdateMessageAndGoBack(navigate);
                return true;
            } else setHasUnsavedChanges(true);
        } else {
            // const res = await Library.makePostRequest("createStore", postData, false, setCreateStoreLoading);
            const res = await Library.makeCommonPostRequest(
                "Stores",
                "createStore",
                postData,
                false,
                setCreateStoreLoading
            );
            if (res?.data?.status === 200) {
                /* setStoreCreatedPopup({
                    ourId: res?.data?.data?.ourID,
                    showPopup: true
                });
                navigate("/stores");

                const updatedNavigations = {
                    ...navigations.navigationAccesses,
                    stores: {
                        ...navigations.navigationAccesses.stores,
                        createModeOnly: false
                    }
                };

                dispatch(setNavigations({ navigationAccesses: updatedNavigations }));
                */
                let currentStores = parseInt(localStorage.getItem("activeStores")) || 0;
                currentStores += 1;
                localStorage.setItem("activeStores", currentStores.toString());

                /* if (currentStores === 1) {
                    //setShowIsStoreSavedPopup(true); //TODO: remove this popup
                } else {
                    const updatedNavigations = getAllUnlockedNavigations(navigations.navigationAccesses);
                    dispatch(setNavigations({ navigationAccesses: updatedNavigations }));
                }

                
                */
                //if user is onboarding...
                if (isUserOnboarding) {
                    onAfterCreateStoreInOnboarding();
                } else {
                    navigate("/stores");

                    Library.showToastMessage({
                        type: "success",
                        title: "Store created!",
                        message: "The store has been successfully created!"
                    });
                }

                return true;
            } else setHasUnsavedChanges(true);
        }
        return false;
    };

    const updateInviteRequest = inviteID => {
        Library.makeCommonPostRequest(
            "Invites",
            "updateInvite",
            { inviteID: inviteID, status: "Enrolled" },
            false,
            setIsLoading
        ).then(res => {
            if (res.data.status !== 200) {
                return;
            }
            localStorage.setItem("status", "Enrolled");
        });
    };

    const onAfterCreateStoreInOnboarding = () => {
        localStorage.setItem("status", "Enrolled");

        setShowStoreWithoutPOSOnboardingCompleted(true);

        const updatedNavigations = getAllUnlockedNavigations(navigations.navigationAccesses);
        dispatch(setNavigations({ navigationAccesses: updatedNavigations }));

        const inviteID = userAppData?.invite?._id;
        if (inviteID) updateInviteRequest(inviteID);
        navigate("/stores");
    };

    const handleGetOpenHours = async ({ triggeredFrom = "" }) => {
        const { storeName } = formCompanyID.current.getAllFieldValues();
        const { googlePlusCode } = formCompanyAddress.current.getAllFieldValues();
        const openHoursFormData = {
            storeName,
            address: googlePlusCode
        };

        const res = await Library.makeCommonPostRequest(
            "Stores",
            "fetchGoogleMapsPlacesData",
            openHoursFormData,
            false,
            setIsOpeningHoursLoading,
            () => {},
            undefined,
            false
        );
        const status = res?.data?.status;
        const isFormInputTrigger = triggeredFrom === triggeredTypes.FORM_INPUT;

        if (status === 200) {
            if (isFormInputTrigger) {
                Library.showToastMessage({
                    type: "success",
                    title: "Success!",
                    message:
                        "Store info completed! Address details and open hours successfully fetched from Google maps!"
                });
            } else {
                Library.showToastMessage({
                    type: "success",
                    title: "Success!",
                    message: "Store hours retrieved and updated successfully!"
                });
            }
            setOpeningHoursError({ error: false, status: 200 });
        } else if (status === 404) {
            if (isFormInputTrigger) setOpeningHoursError({ error: true, status: 404 });
            else {
                Library.showToastMessage({
                    type: "warning",
                    title: "Unable to retrieve store hours",
                    //message: "Please ensure the store is listed on Google Maps."
                    message: "Please insert store open hours manually."
                });
            }
            return;
        } else if (status === 500) {
            if (isFormInputTrigger) setOpeningHoursError({ error: true, status: 500 });
            else {
                Library.showToastMessage({
                    type: "error",
                    title: "Fetching store hours failed",
                    message: "Check your internet connection or insert manually."
                });
            }
            return;
        } else if (status === 401) {
            if (isFormInputTrigger) setOpeningHoursError({ error: true, status: 401 });
            else {
                Library.showToastMessage({
                    type: "warning",
                    title: "Fetching store hours failed",
                    message:
                        "This store name in Google maps is different. Please change store name above or insert time manually."
                });
            }
            return;
        } else {
            if (isFormInputTrigger) setOpeningHoursError({ error: true, status: 404 });
            else {
                Library.showToastMessage({
                    type: "warning",
                    title: "Unable to retrieve store hours",
                    //message: "Please ensure the store is listed on Google Maps."
                    message: "Please insert store open hours manually."
                });
            }
            return;
        }

        const googleData = res?.data?.data;
        const openHours = googleData?.openingHours?.response?.openingHours;

        setStoreFormData(prev => ({
            ...prev,
            storeHoursData: {
                monday: readStoreHoursForDay(openHours, "monday"),
                tuesday: readStoreHoursForDay(openHours, "tuesday"),
                wednesday: readStoreHoursForDay(openHours, "wednesday"),
                thursday: readStoreHoursForDay(openHours, "thursday"),
                friday: readStoreHoursForDay(openHours, "friday"),
                saturday: readStoreHoursForDay(openHours, "saturday"),
                sunday: readStoreHoursForDay(openHours, "sunday")
            },
            googlePlusCode: googleData?.plusCode || prev.googlePlusCode,
            country: googleData?.country || prev.country,
            city: googleData?.city || prev.city,
            postalCode: googleData?.postalCode || prev.postalCode,
            addressLine1: googleData?.address1 || prev.addressLine1,
            addressLine2: googleData?.address2 || prev.addressLine2,
            latitude: googleData?.latitude || prev.latitude,
            longitude: googleData?.longitude || prev.longitude,
            shoppingCenter: googleData?.shoppingCenter === "Yes" ? "true" : "false"
        }));
    };

    const handleTextInputBlur = () => {
        if (openingHoursError.error && openingHoursError.status !== 0 && openingHoursError.status !== 401) {
            Library.showToastMessage({
                type: "warning",
                title: "Unable to retrieve store hours",
                message:
                    "Store info not completed! We could not fetch store details. Please try again or insert data manually."
            });
        } else if (openingHoursError.error && openingHoursError.status === 0) {
            Library.showToastMessage({
                type: "warning",
                title: "Store name missing",
                message: "Please enter store name to get opening hours from google."
            });
        } else if (openingHoursError.error && openingHoursError.status === 401) {
            Library.showToastMessage({
                type: "warning",
                title: "Fetching store hours failed",
                message:
                    "This store name in Google maps is different. Please change store name above or insert time manually."
            });
        }
        setOpeningHoursError({ error: false, status: 0 });
    };

    const getConnectedRetailBrands = async (retailBrandID, stockSharingLabelBrandIDs) => {
        if (!retailBrandID) retailBrandID = JSON.parse(localStorage.getItem("retailBrands"))?.[0]?._id;

        setStockSharingSelected(prev => ({
            ...prev,
            loading: true
        }));

        const res = await Library.makePostRequest("getRetailBrandConnectedBrands", {
            retailBrandID: retailBrandID ? retailBrandID : storeFormData?.retailBrandID
        });

        if (res?.status === 200) {
            const data = res?.data?.data;

            if (stockSharingLabelBrandIDs?.length === 0) {
                //when no stock sharing brands are selected means that the user is actually sharing with all brands
                stockSharingLabelBrandIDs = data?.map(item => item?.labelBrandID) || [];
            }

            const dropdownData = data?.map(item => {
                return {
                    name: item?.labelBrandName,
                    value: item?.labelBrandID,
                    selected: stockSharingLabelBrandIDs?.includes(item?.labelBrandID) || false
                };
            });

            if (dropdownData?.length === 0) {
                dropdownData.push({
                    name: "All label brands",
                    value: "allLabelBrands",
                    selected: !isCreatePage
                });

                setStockSharingToLabelBrands(dropdownData);
            } else
                setStockSharingToLabelBrands(
                    dropdownData?.filter(item => {
                        return stockSharingLabelBrandIDs?.includes(item?.value);
                    }) || []
                );

            setRetailBrandConnectedBrands(dropdownData);
            setStockSharingSelected(prev => ({
                ...prev,
                shareDropdownEnabled: true,
                loading: false
            }));
        } else {
            console.log("Error fetching connected retail brands: ", res?.message);
        }
    };

    const handleOnDropDownChange = value => {
        sessionStorage.setItem("currentRetailBrand", value?.value || "");
        setStoreFormData({
            ...storeFormData,
            retailBrandID: value?.value || "",
            retailBrandName: value?.name || ""
        });
    };

    // #endregion

    // #region FORM FIELDS
    const formCompanyAddress = useRef();
    const formCompanyID = useRef();
    const formStoreHoursRef = useRef();
    const formDeliveryOptionsRef = useRef();

    const formCompanyFieldsRefs = [];

    const formCompanyFields = [
        <FormField
            type="dropdown"
            title="To Brand"
            tooltip="Select a brand to associate"
            requiredType={isUserOnboarding ? "mandatoryLocked" : "mandatory"}
            varName="toBrand"
            ref={el => (formCompanyFieldsRefs.toBrand = el)}
            onTextChange={handleOnDropDownChange}
            value={storeFormData?.retailBrandName}
            dropdownData={retailBrands?.sort((a, b) => a?.name?.localeCompare(b?.name))}
            onDropdownChange={value => {
                setStoreFormData({
                    ...storeFormData,
                    retailBrandID: value?.value,
                    retailBrandName: value?.name
                });

                setStockSharingSelected({
                    shareStockDisabled: false,
                    agreeTerms: false,
                    shareDropdownEnabled: false,
                    loading: false
                });
                setRetailBrandConnectedBrands([]);
                setStockSharingToLabelBrands([]);
            }}
        />,
        <FormField
            type="text"
            title={
                <Tooltip
                    open={showTooltip}
                    onOpenChange={visible => setShowTooltip(visible)}
                    title={
                        <div>
                            <p>
                                If you insert the same name as in Google maps, the address and open hours will be
                                fetched and filled up automatically. If the name can't be found in Google maps, please
                                insert the address and open hours manually.
                            </p>
                            <button
                                className="text-white px-2 py-1 d-none"
                                style={{
                                    backgroundColor: "var(--Buttons-Green-Active)",
                                    outline: "none",
                                    border: "none",
                                    borderRadius: "4px",
                                    marginTop: "10px"
                                }}
                                onClick={() => setShowTooltip(false)}>
                                OK
                            </button>
                        </div>
                    }
                    color="#10141b"
                    overlayInnerStyle={{ textAlign: "center" }}
                    overlayStyle={{
                        fontSize: "0.75rem",
                        maxWidth: "300px",
                        textAlign: "center"
                    }}>
                    <i className="Icon fas fa-info-circle text-align-center" aria-hidden="true"></i>
                    &nbsp; Store Name
                </Tooltip>
            }
            tooltipMaxWidth={300}
            requiredType="mandatory"
            assistiveText={
                googleMatchError &&
                '<p style={{ color: "orange", fontSize: "0.85rem", marginTop: "4px" }}>NO MATCH IN GOOGLE MAPS</p>'
            }
            varName="storeName"
            ref={el => (formCompanyFieldsRefs.storeName = el)}
            value={storeFormData?.storeName}
            onTextChange={val => setStoreFormData({ ...storeFormData, storeName: val })}
            onTextBlur={text => fetchGoogleMapsInfo(text?.trim())}
            onKeyDown={e => {
                if (e.key === "Enter") fetchGoogleMapsInfo(storeFormData?.storeName?.trim());
            }}
        />,
        <FormFieldImageUpload
            title="Store Facade"
            requiredType="mandatory"
            ref={el => (formCompanyFieldsRefs.storeFacade = el)}
            varName="storeFacade"
            imageURL={storeFormData?.storeFacadeData}
        />,
        <FormFieldImageUpload
            title="Store Interior"
            requiredType="recommended"
            ref={el => (formCompanyFieldsRefs.storeInterior = el)}
            varName="storeInterior"
            imageURL={storeFormData?.storeInteriorData}
        />
    ];
    // #endregion

    // #region RENDER
    return (
        <StyledPage>
            <StorePopups
                isOpeningHoursLoading={isOpeningHoursLoading}
                isLoading={isLoading}
                isLoadingStoreData={isLoadingStoreData}
                showTAC={showTAC}
                setShowTAC={setShowTAC}
                showCorrectCompanyPopup={showCorrectCompanyPopup}
                showIsStorePosSavedPopup={showIsStorePosSavedPopup}
                correctCompanyChecked={correctCompanyChecked}
                setCorrectCompanyChecked={setCorrectCompanyChecked}
                setShowCorrectCompanyPopup={setShowCorrectCompanyPopup}
                showDialogBox={showDialogBox}
                setShowDialogBox={setShowDialogBox}
                storeID={storeID}
                onSaveClicked={onSaveClicked}
                createStoreLoading={createStoreLoading}
                editStoreLoading={editStoreLoading}
                setShowIsStorePosSavedPopup={setShowIsStorePosSavedPopup}
            />

            <FormCard
                title="STORE ID"
                ref={formCompanyID}
                numOfFieldsPerLine={2}
                showDefaultRightLegends={true}
                formFields={formCompanyFields}
                formFieldRefs={formCompanyFieldsRefs}
                onFormChanged={checkFormIsValid}
                showUnsavedChangesPopup={true}
            />

            <AddressFormCardStandard
                cardTitle="STORE LOCATION"
                ref={formCompanyAddress}
                pinLabel={storeFormData?.storeName}
                unnamedPinLabel={"Unnamed Store"}
                useGoogleMapsDarkMode={true}
                usesShoppingCenter={true}
                hasKYCAssistiveText={false}
                onFormChanged={checkFormIsValid}
                locationDetails={{
                    googlePlusCode: storeFormData.googlePlusCode,
                    country: storeFormData.country,
                    city: storeFormData.city,
                    postalCode: storeFormData.postalCode,
                    addressOne: storeFormData.addressLine1,
                    addressTwo: storeFormData.addressLine2,
                    latitude: storeFormData.latitude,
                    longitude: storeFormData.longitude,
                    shoppingCenter: storeFormData.shoppingCenter,
                    shoppingCenterName: storeFormData.shoppingCenterName,
                    shoppingCenterStoreNumber: storeFormData.shoppingCenterStoreNumber,
                    shoppingCenterFloor: storeFormData.shoppingCenterFloor
                }}
                handleGetOpenHours={handleGetOpenHours}
                handleTextInputBlur={handleTextInputBlur}
                onLocationDetailsChange={locationDetails => {
                    const {
                        googlePlusCode,
                        country,
                        city,
                        postalCode,
                        addressOne,
                        addressTwo,
                        latitude,
                        longitude,
                        shoppingCenter
                    } = locationDetails;

                    setStoreFormData({
                        ...storeFormData,
                        googlePlusCode: googlePlusCode,
                        country: country,
                        city: city,
                        postalCode: postalCode,
                        addressLine1: addressOne,
                        addressLine2: addressTwo,
                        latitude: latitude,
                        longitude: longitude,
                        shoppingCenter: shoppingCenter
                    });
                }}
            />

            <StoreHours
                title="Store Hours"
                ref={formStoreHoursRef}
                storeHours={storeFormData?.storeHoursData}
                onFormChanged={checkFormIsValid}
                afterTitleComponent={
                    <FetchFromGoogleComponent
                        isBtnEnabled={isOpenHoursBtnEnabled}
                        isLoading={isOpeningHoursLoading}
                        onClick={handleGetOpenHours}
                    />
                }
            />

            <DeliveryOptionsAvailable
                title="Delivery Options Available"
                ref={formDeliveryOptionsRef}
                deliveryOptions={storeFormData}
                onDeliveryOptionsChanged={deliveryOptions => {
                    setStoreFormData({
                        ...storeFormData,
                        pickupEnabled: deliveryOptions.pickupEnabled,
                        courierEnabled: deliveryOptions.courierEnabled,
                        shippingEnabled: deliveryOptions.shippingEnabled
                    });
                }}
                onFormChanged={checkFormIsValid}
            />

            <StoreStockSharing
                setShowTAC={setShowTAC}
                limitedStock={limitedStock}
                setLimitedStock={setLimitedStock}
                fullStock={fullStock}
                setFullStock={setFullStock}
                brandIsInvalid={brandIsInvalid}
                stockSharingSelected={stockSharingSelected}
                setStockSharingSelected={setStockSharingSelected}
                stockSharingToLabelBrands={stockSharingToLabelBrands}
                setStockSharingToLabelBrands={setStockSharingToLabelBrands}
                retailBrandConnectedBrands={retailBrandConnectedBrands}
                getConnectedRetailBrands={getConnectedRetailBrands}
                retailBrandID={JSON.parse(localStorage.getItem("retailBrands"))?.[0]?._id} //TODO: fetch from selected retail brand ID
            />
        </StyledPage>
    );
    // #endregion
}

// #region HELPERS
function setupHeaders(props, saveButtonEnabled, onBtnClicked, storeID, isUserOnboarding, navigations) {
    props.setControlBarLeftBtns([{ to: "#", disabled: true, title: "Stores", icon: "fa-magnifying-glass" }]);
    props.setControlBarCenterBtns([
        { to: "#", active: true, disabled: false, title: "Stores" },
        { to: "/stores/staff", active: false, disabled: true, title: "Staff" },
        { to: "/stores/pos", active: false, disabled: false, title: "POS" }
    ]);

    if (storeID) {
        props.setControlBarRightBtns([
            { to: "/stores", active: false, disabled: false, title: "Back", icon: "chevron-left" },
            { to: "#", active: true, disabled: false, title: "Edit", icon: "pencil-line" }
        ]);
    } else {
        props.setControlBarRightBtns([
            { to: "/stores", active: false, disabled: isUserOnboarding, title: "View", icon: "eye" },
            { to: "/stores/create", active: true, disabled: false, title: "Add", icon: "plus" },
            { to: "#", active: false, disabled: true, title: "Details", icon: "microscope" },
            { to: "#", active: false, disabled: true, title: "Edit", icon: "edit" }
        ]);
    }

    props.setFilterHeaderProperties({
        customContentHeader: (
            <SimpleButtonContentHeader
                btnTitle={!storeID ? "SAVE" : "UPDATE"}
                //btnEnabled={saveButtonEnabled && !isUserOnboarding}
                btnEnabled={saveButtonEnabled}
                onClick={onBtnClicked}
            />
        )
    });
}
// #endregion

// #region small components
const FetchFromGoogleComponent = ({ isLoading, onClick, isBtnEnabled }) => {
    return (
        <div
            className={`frame-1000011239 ${!isBtnEnabled && "disabled-open-hours-btn"}`}
            onClick={() => isBtnEnabled && onClick({ triggeredFrom: triggeredTypes.OPEN_HOURS_BUTTON })}>
            <div className="fetch-from-google-maps">
                {isLoading ? (
                    <>
                        <CircularProgress className="circular-loader" />
                        Updating...
                    </>
                ) : (
                    "Update from Google maps"
                )}
            </div>
        </div>
    );
};
// #endregion
